import { Modal, ModalClose, ModalDialog } from '@mui/joy';
import Button from '@mui/joy/Button';

import { ReactNode, useState } from 'react';

import {
  useCreateTenant,
  useCreateWorkspace,
  useOrgWorkspaces,
  useOrganizations,
  useSelectedWorkspace,
} from '@/hooks/useSwr';
import OrganizationIcon from '@/icons/OrganizationIcon.svg?react';
import { TenantSchema } from '@/types/schema';

import { EditNameForm } from './EditNameForm';

export function CreateWorkspaceModal(props: {
  className?: string;
  onSuccess?: (data: TenantSchema) => void;
  children?: (props: { onClick: () => void }) => ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedWorkspace = useSelectedWorkspace();
  const workspaces = useOrgWorkspaces();
  const createTenant = useCreateWorkspace({
    onSuccess: props.onSuccess,
  });

  const names = workspaces.data?.map((tenant) => tenant.display_name) ?? [];
  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          sx={{ minWidth: 550, paddingX: 5, paddingY: 4 }}
        >
          <ModalClose size="lg" />
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold">Create a workspace</div>
              <div className="w-[450px] text-sm font-normal">
                Workspaces are shared spaces within an organization to
                collaborate with your team.
              </div>
            </div>

            <div className=" w-full">
              <EditNameForm
                existingNames={names}
                nameTitle="Workspace"
                onSubmit={async (name) => {
                  await createTenant.trigger({
                    display_name: name,
                    organization_id:
                      selectedWorkspace.data?.organization_id ?? undefined,
                  });
                  setOpen(false);
                }}
                isSubmitting={createTenant.isMutating}
                submitError={createTenant.error}
              />
            </div>
          </div>
        </ModalDialog>
      </Modal>

      {props.children?.({ onClick: () => setOpen(true) })}
    </>
  );
}

export function CreateOrganizationModal(props: {
  className?: string;
  onSuccess?: (data: TenantSchema | undefined) => void;
  children?: (props: { onClick: () => void }) => ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const orgs = useOrganizations();
  const orgNames = orgs.data?.map((org) => org.display_name) ?? [];

  const createTenant = useCreateTenant({
    onSuccess: props.onSuccess,
  });

  const defaultButton = (
    <Button
      size="sm"
      variant="outlined"
      color="neutral"
      onClick={() => setOpen(true)}
      startDecorator={<OrganizationIcon />}
      className={props.className}
    >
      Create Organization
    </Button>
  );

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          sx={{ minWidth: 550, paddingX: 5, paddingY: 4 }}
        >
          <ModalClose size="lg" />
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold">
                Create an Organization
              </div>
            </div>

            <div className=" w-full">
              <EditNameForm
                existingNames={orgNames}
                nameTitle="Organization"
                onSubmit={async (name, isPersonal) => {
                  await createTenant.trigger({
                    display_name: name,
                    organization_id: undefined,
                    is_personal: isPersonal,
                  });
                  setOpen(false);
                }}
                isSubmitting={createTenant.isMutating}
                submitError={createTenant.error}
              />
            </div>
          </div>
        </ModalDialog>
      </Modal>

      {props.children
        ? props.children({ onClick: () => setOpen(true) })
        : defaultButton}
    </>
  );
}
